package fr.manyverse.app.presentation.components.player_screen.character.choice

import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.GridView
import androidx.compose.material.icons.filled.ViewCarousel
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.IconButtonDefaults
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import fr.manyverse.app.domain.model.PreMadeCharacter
import fr.manyverse.app.presentation.components.premade.PreMadeCarousel
import fr.manyverse.app.presentation.components.premade.PreMadeCharacterCard
import fr.manyverse.app.presentation.components.premade.PreMadeGridCard
import fr.manyverse.app.presentation.components.premade.rememberPreMadeCarouselState
import fr.manyverse.app.presentation.state.AsyncUiState
import fr.manyverse.app.presentation.theme.success
import fr.manyverse.app.presentation.viewmodel.TableCharacterViewModel
import kotlinx.coroutines.launch
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.cancel
import manyverse.shared.generated.resources.choose_premade
import manyverse.shared.generated.resources.premade_choose
import manyverse.shared.generated.resources.premade_pass
import manyverse.shared.generated.resources.premade_view_carousel
import manyverse.shared.generated.resources.premade_view_grid
import org.jetbrains.compose.resources.stringResource


@Composable
fun SelectPreMadeDialog(
    preMades: List<PreMadeCharacter>,
    viewModel: TableCharacterViewModel,
    onDismiss: () -> Unit
) {
    val assignState by viewModel.assignState.collectAsStateWithLifecycle()
    val isLoading = assignState is AsyncUiState.Loading

    LaunchedEffect(assignState) {
        if (assignState is AsyncUiState.Success) {
            viewModel.resetAssignState()
            onDismiss()
        }
    }

    var isGridMode by remember { mutableStateOf(false) }
    var selectedPreMadeIndex: Int? by remember { mutableStateOf(null) }
    val pagerState = rememberPreMadeCarouselState(preMades.size)
    val coroutineScope = rememberCoroutineScope()

    Dialog(
        onDismissRequest = { if (!isLoading) onDismiss() },
        properties = DialogProperties(usePlatformDefaultWidth = false)
    ) {
        Surface(
            modifier = Modifier.fillMaxSize(),
            color = MaterialTheme.colorScheme.background
        ) {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(16.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Text(
                    text = stringResource(Res.string.choose_premade),
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier.padding(vertical = 16.dp)
                )

                if (isLoading) {
                    LinearProgressIndicator(modifier = Modifier.fillMaxWidth())
                }

                if (assignState is AsyncUiState.Error) {
                    Text(
                        text = (assignState as AsyncUiState.Error).message,
                        color = MaterialTheme.colorScheme.error,
                        style = MaterialTheme.typography.bodySmall,
                        modifier = Modifier.padding(vertical = 8.dp)
                    )
                }

                Box(modifier = Modifier.weight(1f).fillMaxWidth()) {
                    if (isGridMode) {
                        if (selectedPreMadeIndex == null) {
                            LazyVerticalGrid(
                                columns = GridCells.Adaptive(minSize = 120.dp),
                                contentPadding = PaddingValues(8.dp),
                                horizontalArrangement = Arrangement.spacedBy(8.dp),
                                verticalArrangement = Arrangement.spacedBy(8.dp),
                                modifier = Modifier.fillMaxSize()
                            ) {
                                items(preMades.size, key = { preMades[it].id }) { index ->
                                    PreMadeGridCard(
                                        preMade = preMades[index],
                                        enabled = !isLoading,
                                        onClick = { selectedPreMadeIndex = index },
                                        modifier = Modifier.fillMaxWidth()
                                    )
                                }
                            }
                        } else {
                            Column(modifier = Modifier.fillMaxSize()) {
                                Box(modifier = Modifier.weight(1f).fillMaxWidth()) {
                                    PreMadeCharacterCard(preMade = preMades[selectedPreMadeIndex!!])
                                }
                                LazyRow(
                                    contentPadding = PaddingValues(8.dp),
                                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .height(160.dp)
                                ) {
                                    itemsIndexed(preMades, key = { _, preMade -> preMade.id }) { index, preMade ->
                                        PreMadeGridCard(
                                            preMade = preMade,
                                            selected = index == selectedPreMadeIndex,
                                            enabled = !isLoading,
                                            onClick = {
                                                selectedPreMadeIndex = if (index == selectedPreMadeIndex) null else index
                                            },
                                            modifier = Modifier.width(100.dp)
                                        )
                                    }
                                }
                            }
                        }
                    } else {
                        PreMadeCarousel(
                            items = preMades,
                            pagerState = pagerState,
                            modifier = Modifier.fillMaxSize()
                        ) { preMade ->
                            PreMadeCharacterCard(preMade = preMade)
                        }
                    }

                    if (isLoading) {
                        CircularProgressIndicator(modifier = Modifier.align(Alignment.Center))
                    }
                }

                Spacer(modifier = Modifier.height(8.dp))

                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(bottom = 16.dp)
                ) {
                    TextButton(
                        onClick = { if (!isLoading) onDismiss() },
                        enabled = !isLoading,
                        modifier = Modifier.align(Alignment.CenterStart)
                    ) {
                        Text(stringResource(Res.string.cancel))
                    }

                    if (!isGridMode) {
                        Row(
                            modifier = Modifier.align(Alignment.Center),
                            horizontalArrangement = Arrangement.spacedBy(24.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            CircleActionButton(
                                onClick = {
                                    coroutineScope.launch {
                                        pagerState.animateScrollToPage(pagerState.currentPage + 1)
                                    }
                                },
                                enabled = !isLoading,
                                icon = { Icon(Icons.Default.Close, contentDescription = null) },
                                label = stringResource(Res.string.premade_pass),
                                color = MaterialTheme.colorScheme.error
                            )
                            CircleActionButton(
                                onClick = {
                                    if (!isLoading && preMades.isNotEmpty()) {
                                        viewModel.selectPreMade(preMades[pagerState.currentPage % preMades.size].id)
                                    }
                                },
                                enabled = !isLoading,
                                icon = { Icon(Icons.Default.Check, contentDescription = null) },
                                label = stringResource(Res.string.premade_choose),
                                color = MaterialTheme.colorScheme.success
                            )
                        }
                    } else if (selectedPreMadeIndex != null) {
                        CircleActionButton(
                            onClick = {
                                if (!isLoading) {
                                    viewModel.selectPreMade(preMades[selectedPreMadeIndex!!].id)
                                }
                            },
                            enabled = !isLoading,
                            icon = { Icon(Icons.Default.Check, contentDescription = null) },
                            label = stringResource(Res.string.premade_choose),
                            color = MaterialTheme.colorScheme.success,
                            modifier = Modifier.align(Alignment.Center)
                        )
                    }

                    CircleActionButton(
                        onClick = { isGridMode = !isGridMode; selectedPreMadeIndex = null },
                        enabled = true,
                        icon = {
                            Icon(
                                if (isGridMode) Icons.Default.ViewCarousel else Icons.Default.GridView,
                                contentDescription = null
                            )
                        },
                        label = stringResource(if (isGridMode) Res.string.premade_view_carousel else Res.string.premade_view_grid),
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        modifier = Modifier.align(Alignment.CenterEnd)
                    )
                }
            }
        }
    }
}

@Composable
private fun CircleActionButton(
    onClick: () -> Unit,
    enabled: Boolean,
    icon: @Composable () -> Unit,
    label: String,
    color: Color,
    modifier: Modifier = Modifier
) {
    val effectiveColor = if (enabled) color else color.copy(alpha = 0.38f)
    Column(
        modifier = modifier,
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        IconButton(
            onClick = onClick,
            enabled = enabled,
            colors = IconButtonDefaults.iconButtonColors(
                containerColor = Color.Transparent,
                contentColor = color,
                disabledContainerColor = Color.Transparent,
                disabledContentColor = color.copy(alpha = 0.38f)
            ),
            modifier = Modifier
                .size(60.dp)
                .border(1.5.dp, effectiveColor, CircleShape)
                .clip(CircleShape)
        ) {
            icon()
        }
        Text(
            text = label,
            style = MaterialTheme.typography.labelSmall,
            color = effectiveColor
        )
    }
}
